/**
 * DEV ONLY. A full-screen "Test Ad" overlay in plain DOM, with a countdown and a
 * close button, so every rewarded/interstitial flow can be played without an ad
 * network. `?ads=nofill|fail|slow` (devAdsModeFromQuery) simulates the bad paths.
 */
import { nonPersonalized, type AdSlot, type AdsInitOptions, type AdsProvider, type RewardedResult } from './types.js';

export type DevAdsMode = 'fill' | 'nofill' | 'fail' | 'slow';

export interface DevAdsOptions {
  mode?: DevAdsMode;
  /** countdown length; the reward is earned when it reaches 0 */
  seconds?: number;
  /** extra "loading" time in `slow` mode */
  slowMs?: number;
  document?: Document;
}

export function devAdsModeFromQuery(search: string): DevAdsMode {
  const v = new URLSearchParams(search).get('ads');
  return v === 'nofill' || v === 'fail' || v === 'slow' ? v : 'fill';
}

const OVERLAY_CSS =
  'position:fixed;inset:0;z-index:2147483647;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;' +
  'background:rgba(20,16,32,.94);color:#fff;font:600 18px/1.4 system-ui,sans-serif;text-align:center;padding:24px';
const BUTTON_CSS =
  'position:absolute;top:max(16px,env(safe-area-inset-top));right:16px;min-width:44px;min-height:44px;padding:8px 14px;border:0;border-radius:22px;' +
  'background:#fff;color:#201a30;font:600 16px system-ui,sans-serif;cursor:pointer';

export class DevAdsProvider implements AdsProvider {
  readonly network = 'dev';
  private opts: AdsInitOptions = { personalized: false, childDirected: false };

  constructor(private readonly o: DevAdsOptions = {}) {}

  get mode(): DevAdsMode {
    return this.o.mode ?? 'fill';
  }

  async init(opts: AdsInitOptions): Promise<void> {
    this.opts = opts;
  }

  async isRewardedReady(_slot: AdSlot): Promise<boolean> {
    return this.mode !== 'nofill';
  }

  async showRewarded(slot: AdSlot, nonce: string): Promise<RewardedResult> {
    if (this.mode === 'nofill') return { completed: false, network: this.network, error: 'nofill', detail: 'dev: no fill' };
    if (this.mode === 'fail') return { completed: false, network: this.network, error: 'failed', detail: 'dev: load failed' };
    const watched = await this.present(`Rewarded · ${slot}`, true);
    return watched
      ? { completed: true, network: this.network, proof: `dev:${nonce}` }
      : { completed: false, network: this.network, error: 'dismissed' };
  }

  async showInterstitial(): Promise<{ shown: boolean }> {
    if (this.mode === 'nofill' || this.mode === 'fail') return { shown: false };
    await this.present('Interstitial', false);
    return { shown: true };
  }

  /**
   * Resolves true when the countdown finished before the overlay closed. Rewarded ads
   * can be skipped early (no reward); interstitials unlock their close button at 0.
   */
  private present(label: string, skippable: boolean): Promise<boolean> {
    const doc = this.o.document ?? document;
    const seconds = this.o.seconds ?? 5;
    const root = doc.createElement('div');
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Test Ad');
    root.dataset.mpAd = 'overlay';
    root.style.cssText = OVERLAY_CSS;
    const title = doc.createElement('div');
    title.textContent = 'Test Ad';
    title.style.cssText = 'font-size:32px;letter-spacing:.04em';
    const sub = doc.createElement('div');
    sub.textContent = `${label} · ${nonPersonalized(this.opts) ? 'contextual' : 'personalised'}${this.opts.childDirected ? ' · child-directed' : ''}`;
    const count = doc.createElement('div');
    count.dataset.mpAd = 'countdown';
    count.setAttribute('aria-live', 'polite');
    const close = doc.createElement('button');
    close.type = 'button';
    close.dataset.mpAd = 'close';
    close.style.cssText = BUTTON_CSS;
    root.append(title, sub, count, close);
    doc.body.appendChild(root);

    let left = seconds;
    let done = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const render = () => {
      count.textContent = done ? (skippable ? 'Reward earned!' : 'Thanks for watching') : left < 0 ? 'Loading…' : `${left} s`;
      close.textContent = done ? 'Close ✕' : skippable ? 'Skip (no reward)' : '…';
      close.disabled = !done && !skippable;
      close.setAttribute('aria-label', done ? 'Close ad' : 'Skip ad');
    };
    const tick = () => {
      left -= 1;
      done = left <= 0;
      render();
      if (!done) timer = setTimeout(tick, 1000);
    };
    const start = () => {
      left = seconds;
      render();
      timer = setTimeout(tick, 1000);
    };

    if (this.mode === 'slow') {
      left = -1;
      render();
      timer = setTimeout(start, this.o.slowMs ?? 4000);
    } else {
      start();
    }

    return new Promise<boolean>((resolve) => {
      close.addEventListener('click', () => {
        clearTimeout(timer);
        root.remove();
        resolve(done);
      });
    });
  }
}
